import {gql} from '@apollo/client';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  isInaccessible,
  areAllAccessible,
  getAccessibleOrInaccessibleId,
} from 'graphql-client/InaccessibleInfo';
import {ProcessOutputSubmitParams} from 'notebook/structured-tables/action-functions/submitProcessOutputsActionFunction';
import notifications from 'notifications';
import {CreateOrUpdateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';
import SubmitOutputsConfirmationModal from 'processes/modals/submit-outputs-confirmation/SubmitOutputsConfirmationModal';
import {
  SubmitOutputsConfirmationQuery,
  SubmitOutputsConfirmationQueryVariables,
} from 'processes/tables/__generated/SubmitOutputsConfirmationQuery';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import * as ModalContainer from 'ui-library/ModalContainer';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {CreateServerProcessOutput, UpdateServerProcessOutput} from '../dao/ProcessManagementDAO';

type ProcessOutput = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

type ProcessTask = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processTasks'][number],
  'ProcessTaskNode'
>;

export const SUBMIT_OUTPUT_CONFIRMATION_QUERY = gql`
  query SubmitOutputsConfirmationQuery($taskIds: [StrictString!]!, $outputIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTasks(ids: $taskIds) {
      ... on ProcessTaskNode {
        ...ProcessTaskGraphQL
        schema {
          ...ProcessTaskSchemaGraphQL
          statusLifecycle {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    processOutputs(ids: $outputIds) {
      ... on ProcessOutputNode {
        id
        humanId
        fields
        task {
          id
          humanId
          schema {
            ...ProcessTaskSchemaGraphQL
            statusLifecycle {
              ...ProcessTaskStatusLifecycleGraphQL
            }
          }
        }
        lineageEdgesFrom {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTask').fragment}
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function isUpdateServerProcessOutput(
  output: CreateOrUpdateServerProcessOutput
): output is UpdateServerProcessOutput {
  return (output as UpdateServerProcessOutput).id !== undefined;
}

function isOutputAlreadyMappedBeingUpdated(
  output: UpdateServerProcessOutput,
  serverOutput: ProcessOutput
): boolean {
  assert(output.id === serverOutput.id, 'Expected outputs to match');
  return (
    serverOutput.lineageEdgesFrom.length > 0 && !isEqual(getTagsToSet(serverOutput.fields), output.fields)
  );
}

async function getConfirmationModalInputs(outputs: Array<CreateOrUpdateServerProcessOutput>): Promise<{
  tasks: Array<ProcessTask>;
  outputs: Array<ProcessOutput>;
  outputsBeingCreated: Array<CreateServerProcessOutput>;
  outputsAlreadyMappedWithUpdatedFields: Array<UpdateServerProcessOutput>;
} | null> {
  const outputsBeingCreated: Array<CreateServerProcessOutput> = [];
  const outputsBeingUpdated: Array<UpdateServerProcessOutput> = [];
  const taskIds: Array<string> = [];
  const outputIds: Array<string> = [];
  outputs.forEach((output) => {
    if (isUpdateServerProcessOutput(output)) {
      outputsBeingUpdated.push(output);
      outputIds.push(output.id);
    } else {
      outputsBeingCreated.push(output);
      if (!taskIds.includes(output.processTaskId)) {
        taskIds.push(output.processTaskId);
      }
    }
  });

  const result = await doQuerySafe<SubmitOutputsConfirmationQuery, SubmitOutputsConfirmationQueryVariables>(
    {query: SUBMIT_OUTPUT_CONFIRMATION_QUERY},
    {variables: {taskIds, outputIds}}
  );

  const resultProcessTasks = result.processTasks;

  assert(areAllAccessible(resultProcessTasks), 'All tasks must be accessible.');
  assert(areAllAccessible(result.processOutputs), 'All outputs must be accessible.');

  if (
    resultProcessTasks.some(
      (task) =>
        getAccessibleOrInaccessibleId(task.taskSchema) !==
        getAccessibleOrInaccessibleId(resultProcessTasks[0].taskSchema)
    )
  ) {
    // This is possible if you paste in tasks from a different entry
    notifications.error('All tasks must be of the same schema');
    return null;
  }

  const tasksWithNotInProgressStatus = result.processTasks.filter(
    (task: ProcessTask) => task.status.statusType !== ProcessTaskStatusType.IN_PROGRESS
  );
  const serverOutputsById = result.processOutputs.reduce(
    (acc, curr: ProcessOutput) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as {[id: string]: ProcessOutput}
  );

  const outputsAlreadyMappedWithUpdatedFields = outputsBeingUpdated.filter((output) =>
    isOutputAlreadyMappedBeingUpdated(output, serverOutputsById[output.id])
  );
  if (outputsBeingCreated.length > 0 && tasksWithNotInProgressStatus.length > 0) {
    notifications.error('Tasks corresponding to created outputs must be in an in-progress state.');
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CREATED_OUTPUT_TERMINAL_TASK_ATTEMPT, {
      process_client_origin: !isInaccessible(tasksWithNotInProgressStatus[0])
        ? tasksWithNotInProgressStatus[0].schema.clientOrigin
        : null,
    });
    return null;
  }
  return {
    tasks: resultProcessTasks,
    outputs: result.processOutputs,
    outputsBeingCreated,
    outputsAlreadyMappedWithUpdatedFields,
  };
}

export default async function submitOutputsWithConfirmation({
  outputs,
  onRequestSubmit,
}: {
  outputs: Array<CreateOrUpdateServerProcessOutput>;
  onRequestSubmit: (outputSubmitParams: ProcessOutputSubmitParams) => Promise<void>;
}): Promise<void> {
  const modalParams = await withLoadingScreen(getConfirmationModalInputs(outputs), 'Submitting outputs');
  if (modalParams == null) {
    return;
  }

  if (
    modalParams.outputsBeingCreated.length > 0 ||
    modalParams.outputsAlreadyMappedWithUpdatedFields.length > 0
  ) {
    // Wrap submission function with try/catch to ensure modal is closed on server error.
    const modalKey = _.uniqueId('process-output-confirmation-modal');
    const onRequestSubmitWithModalClose = async (
      outputSubmitParams: ProcessOutputSubmitParams
    ): Promise<void> => {
      try {
        await onRequestSubmit(outputSubmitParams);
      } catch (e) {
        ModalContainer.close(modalKey);
        throw e;
      }
    };

    runModal(SubmitOutputsConfirmationModal, {
      ...modalParams,
      allOutputsForServer: outputs,
      onRequestSubmit: onRequestSubmitWithModalClose,
      modalKey,
    });
  } else {
    await onRequestSubmit({
      processOutputs: outputs,
    });
  }
}
